import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';

import { empty, Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { UsuarioService } from './usuario/usuario/usuario.service';
import { InterceptorSkipHeader } from './../core/framework/http-interceptor-skipheader.model';
import { environment } from '../../environments/environment';
import { ErrorHandlerService } from '../core/framework/error-handler.service';
import { Usuario } from './usuario/usuario/usuario.model';
import { ToasterService } from '../core/framework/toaster/toaster.service';
import { AmbienteConfig } from '../core/multi-tenancy/ambiente.config';
import { Tenant } from './multi-tenancy/tenant/model/tenant.model';
import { CatalogoUsuarioService } from './multi-tenancy/catalogo-usuario/catalogo-usuario.service';
import { CatalogoUsuarioFilter } from './multi-tenancy/catalogo-usuario/model/catalogo-usuario.filter';
import { Status } from '../core/framework/default.model';
import { ListaResultset } from '../core/list/lista-resultset.interface';
import { CatalogoUsuario } from './multi-tenancy/catalogo-usuario/model/catalogo-usuario.model';
import { TipoPerfilDeAcesso } from './perfil-de-acesso/model/tipo-perdil-de-acesso.enum';

@Injectable({
  providedIn: 'root'
})
export class AutenticacaoService {

  constructor(private httpClient: HttpClient,
              private router: Router,
              private toasterService: ToasterService,
              private usuarioService: UsuarioService,
              private catalogoUsuarioService: CatalogoUsuarioService,
              private errorHandlerService: ErrorHandlerService) { }

  toastAcessoNegado(): void {
    this.toasterService.warning('Não autorizado', 'Credencial inválida.');
  }

  login(login: string, senha: string): Observable<boolean> {
    const toasterId = this.toasterService.loading();

    const keyds  = AmbienteConfig.getKeyDS();
    const apiURL = AmbienteConfig.getApiURL();

    const httpOptions = {
                          headers: new HttpHeaders({
                            'Content-Type':  'application/x-www-form-urlencoded',
                            'Authorization': 'Basic ' + environment.credencial_api_authorization_header_basic,
                            'KeyDS': keyds
                          })
                          .append(InterceptorSkipHeader, '')
                          , withCredentials: true
                        };
    const body = `grant_type=password&username=${login}&password=${senha}`;

    return this.httpClient.post(apiURL + '/oauth/token', body, httpOptions)
      .pipe(
        map((response: any) => {
            const _usuario: Usuario = new Usuario();
            _usuario.email = response.email;
            _usuario.nome = response.nome;
            _usuario.id = response.id;
            _usuario.idExterno = response.idExterno;
            localStorage.setItem('usuario', JSON.stringify(_usuario));
            this.salvarTokenDeAcesso(response.access_token);

            return _usuario;
        })
        , switchMap((usuario: Usuario) => {
            // carregar logo em seguida mais informações a partir do serviço usuarioService.fetch()
            return this.usuarioService
                       .fetch(usuario.id)
                       .pipe(
                          switchMap((usuario: Usuario) => {
                            // se for usuario master nao validar organizacao
                            if ( usuario.perfil.tipoPerfil === TipoPerfilDeAcesso.OWNER ) {
                              return of(true);
                            }

                            // validar se tenant do usuário está ativo
                            return this.catalogoUsuarioService
                                      .buscarPorUidETenantAtivo(usuario.uidCatalogoUsuario)
                                      .pipe(
                                        map((catalogoUsuario: CatalogoUsuario) => {
                                          if ( catalogoUsuario ) {
                                            localStorage.setItem('usuario', JSON.stringify(usuario));
                                            return of(true);
                                          }

                                          return of(false);
                                        })
                                        , catchError(err => {
                                          this.toasterService.clearWait(toasterId);
                                          this.logout();

                                          return of(false);;
                                        })
                                      )
                          })
                        )
        })
        , switchMap ((sucessoOuErro: boolean) => {
          return of(sucessoOuErro);
        })
        , catchError(response => {
            this.toasterService.clearWait();
            if (response && response.error && response.error.error === 'invalid_grant') {
              this.toastAcessoNegado();
            }
            else {
              this.errorHandlerService.handle(response);
            }
            return empty();
        })
      );
  }

  usuarioEstaLogado(): boolean {
    return this.tokenDeAcessoEstaValido();
  }

  tokenDeAcessoEstaValido(): boolean {
    const token = localStorage.getItem('auth-token');
    return this.verificaValidadeDoToken(token);
  }

  verificaValidadeDoToken(token: string): boolean {
    if (token == null || token === 'undefined' || token === '') {
      return false;
    }
    if (!new JwtHelperService().isTokenExpired(token)) {
      return true;
    }
    return false;
  }

  salvarTokenDeAcesso(token: string) {
    localStorage.setItem('auth-token', token);
  }

  getTokenDeAcessoValido() {
    if (this.tokenDeAcessoEstaValido()) {
        return localStorage.getItem('auth-token');
    }
    return null;
  }

  getTokenDeAcesso(): string {
    return localStorage.getItem('auth-token');
  }

  limparTokenDeAcesso(): void {
    localStorage.removeItem('auth-token');
  }

  limparRefreshToken(): void {
    const cookieName = 'refreshToken';
    document.cookie = cookieName + '=; Max-Age=-99999999;';
  }

  getCookie(name){
    return document.cookie.split(';').some(c => {
        return c.trim().startsWith(name + '=');
    });
}

  logout() {
    this.limparTokenDeAcesso();
    this.limparRefreshToken();
    this.router.navigate([environment.caminho_pagina_login]);
  }

  getTokenDeAcessoPeloRefreshToken(): Observable<any> {
    const httpOptions = {
                          headers: new HttpHeaders({
                            'Content-Type':  'application/x-www-form-urlencoded',
                            'Authorization': 'Basic ' + environment.credencial_api_authorization_header_basic,
                            'KeyDS': AmbienteConfig.getKeyDS()
                          })
                          .append(InterceptorSkipHeader, '')
                          , withCredentials: true
                        };

    const body = 'grant_type=refresh_token';
    return this.httpClient.post(`${AmbienteConfig.getApiURL()}/oauth/token`, body, httpOptions)
        .pipe(
          map ((response: any) => {
            this.salvarTokenDeAcesso(response.access_token);
            return response.access_token;
          })
          , catchError(response => {
              this.errorHandlerService.handle(response);
              return empty();
          })
        );
  }

}
